/**
 * Small HTML bar charts, shared beyond Customer-360 (Pool Reports, 30/7, imports `BarChart` too). Three
 * shapes, all HTML-not-SVG (crisp value labels, an sr-only data table for screen-reader/keyboard
 * users, dense-range label thinning):
 *
 *  - `StackedBarChart` — multi-segment bars (Reports: Zoom Phone inbound/outbound per period, and the
 *    Insights phone trend), each bar stacking `segments` to a shared y-scale.
 *  - `BarChart` — single-series bars (Customer Reports' own trend charts; Pool Reports' Utilization
 *    trend, 30/7). Extracted from ReportsTab.tsx so both places share one implementation instead of a
 *    second hand-copied version quietly drifting from it.
 *  - `DivergingBarChart` — signed daily change around a zero line (Pool Reports).
 *
 * TOOLTIPS (wave 5, owner 2026-09-19: "slow, and wrong"). ONE mechanism for every chart here, for
 * Insights' `MiniBarChart` and for the Standard report's `CalendarHeatmap` (`useChartTooltip`): each
 * bar — and each SEGMENT of a stacked bar — is its own hover/focus target with its own aria-label, and
 * shows an instant custom tooltip (period · series: value) positioned at the pointer (or above the
 * focused segment). No native `title` (browsers delay it ~1 s and it could only describe the whole
 * stacked bar), no timers, no dependency. The bubble is absolutely positioned inside the figure (no
 * layout shift), pointer-events-none, hidden in print, and dismissed on leave / blur / Escape.
 * Keyboard: one roving tab stop per chart, arrows move between bars/segments.
 */

import { formatInt } from "@/lib/format";
import { cn } from "@/lib/cn";
import { TARGET_FOCUS, TONE_DOT, useChartTooltip, type ChartTone } from "./chartTooltip";

export type { ChartTone };

const TONE_FILL: Record<ChartTone, string> = {
  brand: "bg-(--brand)",
  info: "bg-(--info-solid)",
  warn: "bg-(--warn-solid)",
  ok: "bg-(--ok)",
};

export interface BarSegment {
  /** null = no data for this period (e.g. before a feed's coverage floor) — never drawn as a 0. A bar
   *  whose segments are ALL null renders the muted no-data stub, like BarChart's null. */
  value: number | null;
  tone: ChartTone;
  /** Series name (a11y / sr-only table column). */
  label: string;
}

export interface ChartBar {
  /** x-axis label (e.g. a date). */
  label: string;
  segments: BarSegment[];
}

export interface LegendItem {
  tone: ChartTone;
  label: string;
}

/** Bar-fill identity for the single-series `BarChart` below — a NARROWER tone set than `ChartTone`
 *  (brand/neutral/violet only, no info/warn) because this chart's three call sites (Reports tab trend
 *  charts, Pool Reports' Utilization trend) are never status-toned; brand stays reserved for the
 *  primary series so stacked/adjacent charts don't all read as the same chart repeated. Kept distinct
 *  from `TONE_FILL` above rather than merged into it — the two chart shapes (stacked vs. single-series)
 *  independently evolving their own tone vocabularies is deliberate, not an oversight. */
export type BarChartTone = "brand" | "neutral" | "violet";

const BAR_TONE_FILL: Record<BarChartTone, string> = {
  brand: "bg-(--brand)",
  neutral: "bg-(--data-neutral-solid)",
  violet: "bg-(--data-violet-solid)",
};

/**
 * HTML bar chart (not SVG) so the value label on each bar renders crisp and unstretched. Shows the
 * number above every bar when the set is small enough to stay legible; falls back to bars + tooltips +
 * an sr-only table for dense day-by-day ranges.
 *
 * Extracted from the Customer Reports tab (ReportsTab.tsx) 30/7 so the Pool Reports tab's Utilization
 * trend chart can share it verbatim instead of forking a second copy — ReportsTab's own rendering is
 * unchanged by the move (same markup, same classes, same dense-mode threshold), only its import.
 */
export function BarChart({
  bars,
  caption,
  unit,
  tone = "brand",
}: {
  /** `value: null` means "no data for this bar" (e.g. a day with 0 seats, whose utilization is
   *  genuinely unknown, not 0%) — rendered as a muted no-data stub, never coerced into a false zero
   *  bar (SEAM-2, 31/7). Still emitted as a bar (not filtered out) so the x-axis keeps its real
   *  cadence — dropping null days would compress the remaining bars and mislead the date scale. */
  bars: { label: string; value: number | null }[];
  caption: string;
  unit: string;
  tone?: BarChartTone;
}) {
  const fill = BAR_TONE_FILL[tone];
  const { figureRef, target, bubble } = useChartTooltip();
  // The y-scale max is computed over KNOWN values only — a null bar has no height claim to make on
  // the scale (a lone null day is not "0", it doesn't get to drag the axis toward 0 either).
  const known = bars.map((b) => b.value).filter((v): v is number => v != null);
  const max = Math.max(1, ...known);
  const dense = bars.length > 18; // hide per-bar text when there are too many to read

  return (
    <figure
      ref={figureRef}
      className="relative flex flex-col gap-2 rounded-(--radius) border border-(--border) bg-(--surface) p-4 shadow-(--shadow-sm)"
    >
      <figcaption className="sr-only">{caption}</figcaption>
      <div className="flex flex-col gap-1">
        {/* Dense charts hide the per-bar value labels; a max-value gridline restores the missing scale reference. */}
        {dense && (
          <div className="flex items-center gap-2">
            <span className="tabular text-(length:--text-2xs) font-medium leading-none text-(--text-muted)">
              {formatInt(max)}
            </span>
            <span className="h-px flex-1 bg-(--border)" aria-hidden />
          </div>
        )}
        <div className="flex h-56 items-end gap-1.5">
          {bars.map((b, i) => {
            // A local const (not just a derived boolean) so TS narrows `value` itself in every branch
            // below — a separately-named `hasValue` boolean does not propagate the null-check back onto
            // `b.value`.
            const value = b.value;
            return (
              <div
                key={`${b.label}:${i}`}
                // max-w caps sparse charts: 1-3 data points must read as a few honest bars, not one
                // full-width slab (Pool Reports' first live day rendered exactly that, 31/7).
                className={cn(
                  "flex h-full min-w-0 max-w-14 flex-1 flex-col items-center justify-end gap-1",
                  TARGET_FOCUS,
                )}
                {...target(
                  `b${i}`,
                  {
                    period: b.label,
                    series: unit,
                    value: value != null ? formatInt(value) : "—",
                  },
                  { col: i },
                )}
              >
                {!dense && (
                  <span className="tabular text-(length:--text-2xs) font-semibold text-(--text-muted)">
                    {value != null ? formatInt(value) : "—"}
                  </span>
                )}
                {value != null ? (
                  <div
                    className={`w-full rounded-t-(--radius-sm) ${fill} transition-[height] duration-(--dur) ease-(--ease-out)`}
                    style={{ height: `${Math.max(1.5, (value / max) * 100)}%` }}
                  />
                ) : (
                  // No-data stub: a flat baseline in the border colour, never the series fill — must
                  // read as "no bar" at a glance, not as a real (near-zero) value.
                  <div
                    className="w-full rounded-t-(--radius-sm) bg-(--border)"
                    style={{ height: "4%" }}
                    aria-hidden
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex gap-1.5">
        {bars.map((b, i) => (
          <span
            key={`${b.label}:x:${i}`}
            className="tabular min-w-0 max-w-14 flex-1 truncate text-center text-(length:--text-2xs) text-(--text-muted)"
          >
            {/* Dense ranges blank the middle labels but keep the first + last so the span stays readable. */}
            {dense ? (i === 0 || i === bars.length - 1 ? b.label : "") : b.label}
          </span>
        ))}
      </div>
      <table className="sr-only">
        <caption>{caption}</caption>
        <thead>
          <tr>
            <th>{caption}</th>
            <th>{unit}</th>
          </tr>
        </thead>
        <tbody>
          {bars.map((b, i) => (
            <tr key={`${b.label}:sr:${i}`}>
              <td>{b.label}</td>
              <td>{b.value ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {bubble}
    </figure>
  );
}

/**
 * Diverging daily-change bars around a zero hairline: positive up, negative down. `null` means "no
 * directly-preceding day to compare against" (first day of a range, or the previous day's snapshot is
 * missing) and renders as the same muted no-data stub BarChart uses — never a zero-height bar, a 0
 * here would claim "nothing changed" where the truth is "no basis to compare" (the ledger does not
 * invent numbers). ONE series tone for BOTH directions on purpose: direction is already carried by
 * position against the zero line and the signed label, and the Meaningful Color Rule reserves
 * green/red for status — a freed licence is ordinary churn, not an alarm.
 */
export function DivergingBarChart({
  bars,
  caption,
  unit,
  tone = "brand",
}: {
  bars: { label: string; value: number | null }[];
  caption: string;
  unit: string;
  tone?: BarChartTone;
}) {
  const fill = BAR_TONE_FILL[tone];
  const known = bars.map((b) => b.value).filter((v): v is number => v != null);
  const maxAbs = Math.max(1, ...known.map((v) => Math.abs(v)));
  const dense = bars.length > 18;
  // Signed display: an explicit "+" on gains so a column of deltas reads as movement at a glance;
  // negatives keep formatInt's own minus so the chart agrees digit-for-digit with the tables.
  const signed = (v: number) => (v > 0 ? `+${formatInt(v)}` : formatInt(v));
  const { figureRef, target, bubble } = useChartTooltip();

  return (
    <figure
      ref={figureRef}
      className="relative flex flex-col gap-2 rounded-(--radius) border border-(--border) bg-(--surface) p-4 shadow-(--shadow-sm)"
    >
      <figcaption className="sr-only">{caption}</figcaption>
      <div className="flex flex-col gap-1">
        {/* Dense charts hide per-bar labels; ± gridline values restore the missing scale reference. */}
        {dense && (
          <div className="flex items-center gap-2">
            <span className="tabular text-(length:--text-2xs) font-medium leading-none text-(--text-muted)">
              {`+${formatInt(maxAbs)}`}
            </span>
            <span className="h-px flex-1 bg-(--border)" aria-hidden />
          </div>
        )}
        <div className="relative flex h-56 gap-1.5">
          {/* The zero baseline — one continuous hairline behind every column. */}
          <div className="absolute inset-x-0 top-1/2 h-px bg-(--border-strong)" aria-hidden />
          {bars.map((b, i) => {
            const value = b.value;
            return (
              <div
                key={`${b.label}:${i}`}
                // Same sparse-data cap as BarChart: a few days must not render as full-width slabs.
                className={cn(
                  "flex h-full min-w-0 max-w-14 flex-1 flex-col items-center",
                  TARGET_FOCUS,
                )}
                {...target(
                  `d${i}`,
                  {
                    period: b.label,
                    series: unit,
                    value: value == null ? "—" : signed(value),
                  },
                  { col: i },
                )}
              >
                {/* Top half: gains grow downward-anchored at the zero line. */}
                <div className="flex w-full flex-1 flex-col items-center justify-end gap-1">
                  {!dense && (value == null || value >= 0) && (
                    <span className="tabular text-(length:--text-2xs) font-semibold text-(--text-muted)">
                      {value == null ? "—" : signed(value)}
                    </span>
                  )}
                  {value != null && value > 0 && (
                    <div
                      className={`w-full rounded-t-(--radius-sm) ${fill} transition-[height] duration-(--dur) ease-(--ease-out)`}
                      style={{ height: `${Math.max(3, (value / maxAbs) * 100)}%` }}
                    />
                  )}
                  {value == null && (
                    // No-comparison stub sits ON the baseline, in the border colour — reads as
                    // "no bar", not as a tiny real value (same contract as BarChart's null).
                    <div
                      className="w-full rounded-t-(--radius-sm) bg-(--border)"
                      style={{ height: "6%" }}
                      aria-hidden
                    />
                  )}
                </div>
                {/* Bottom half: losses hang from the zero line. */}
                <div className="flex w-full flex-1 flex-col items-center justify-start gap-1">
                  {value != null && value < 0 && (
                    <div
                      className={`w-full rounded-b-(--radius-sm) ${fill} transition-[height] duration-(--dur) ease-(--ease-out)`}
                      style={{ height: `${Math.max(3, (Math.abs(value) / maxAbs) * 100)}%` }}
                    />
                  )}
                  {!dense && value != null && value < 0 && (
                    <span className="tabular text-(length:--text-2xs) font-semibold text-(--text-muted)">
                      {signed(value)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {dense && (
          <div className="flex items-center gap-2">
            <span className="tabular text-(length:--text-2xs) font-medium leading-none text-(--text-muted)">
              {`-${formatInt(maxAbs)}`}
            </span>
            <span className="h-px flex-1 bg-(--border)" aria-hidden />
          </div>
        )}
      </div>
      <div className="flex gap-1.5">
        {bars.map((b, i) => (
          <span
            key={`${b.label}:x:${i}`}
            className="tabular min-w-0 max-w-14 flex-1 truncate text-center text-(length:--text-2xs) text-(--text-muted)"
          >
            {dense ? (i === 0 || i === bars.length - 1 ? b.label : "") : b.label}
          </span>
        ))}
      </div>
      <table className="sr-only">
        <caption>{caption}</caption>
        <thead>
          <tr>
            <th>{caption}</th>
            <th>{unit}</th>
          </tr>
        </thead>
        <tbody>
          {bars.map((b, i) => (
            <tr key={`${b.label}:sr:${i}`}>
              <td>{b.label}</td>
              <td>{b.value == null ? "—" : signed(b.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {bubble}
    </figure>
  );
}

/**
 * A stacked (or single-series) vertical bar chart over `bars`. Each bar stacks its segments; the y-scale
 * is the max stacked total. A dense range (> 18 bars — e.g. a 30/90-day daily series) hides per-bar
 * value text and thins the x labels to first + last, with a max-value gridline for scale. The sr-only
 * table exposes every value for non-visual users. Each SEGMENT is its own tooltip / focus target, so
 * pointing at the inbound slice says inbound and at the outbound slice says outbound. A bar whose
 * segments are all null is "no data" (muted stub, "—"), never a 0.
 */
export function StackedBarChart({
  bars,
  caption,
  legend,
  formatValue = formatInt,
}: {
  bars: ChartBar[];
  caption: string;
  legend?: LegendItem[];
  formatValue?: (n: number) => string;
}) {
  const seriesLabels = legend?.map((l) => l.label) ?? bars[0]?.segments.map((s) => s.label) ?? [];
  const noData = bars.map((b) => b.segments.length > 0 && b.segments.every((s) => s.value == null));
  const totals = bars.map((b) => b.segments.reduce((sum, s) => sum + (s.value ?? 0), 0));
  const max = Math.max(1, ...totals);
  const dense = bars.length > 18;
  const { figureRef, target, bubble } = useChartTooltip();

  return (
    <figure
      ref={figureRef}
      className="relative flex flex-col gap-2 rounded-(--radius) border border-(--border) bg-(--surface) p-4 shadow-(--shadow-sm)"
    >
      <figcaption className="sr-only">{caption}</figcaption>

      {legend && legend.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {legend.map((l) => (
            <span
              key={l.label}
              className="flex items-center gap-1.5 text-(length:--text-2xs) text-(--text-muted)"
            >
              <span className={`size-2.5 rounded-[2px] ${TONE_DOT[l.tone]}`} aria-hidden />
              {l.label}
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-1">
        {dense && (
          <div className="flex items-center gap-2">
            <span className="tabular text-(length:--text-2xs) font-medium leading-none text-(--text-muted)">
              {formatValue(max)}
            </span>
            <span className="h-px flex-1 bg-(--border)" aria-hidden />
          </div>
        )}
        <div className="flex h-56 items-end gap-1.5">
          {bars.map((b, bi) => {
            const total = totals[bi] ?? 0;
            const empty = noData[bi] ?? false;
            return (
              <div
                key={`${b.label}:${bi}`}
                className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1"
              >
                {!dense && (
                  <span className="tabular text-(length:--text-2xs) font-semibold text-(--text-muted)">
                    {empty ? "—" : formatValue(total)}
                  </span>
                )}
                {empty ? (
                  // No-data stub (same contract as BarChart's null): border colour, not a series fill.
                  <div
                    className={cn("w-full rounded-t-(--radius-sm) bg-(--border)", TARGET_FOCUS)}
                    style={{ height: "4%" }}
                    {...target(
                      `s${bi}`,
                      { period: b.label, series: caption, value: "—" },
                      { col: bi },
                    )}
                  />
                ) : (
                  /* A stacked column: each segment is a slice sized by its share of the y-scale max,
                     and each is its OWN tooltip target (label + value + period). */
                  <div className="flex w-full flex-1 flex-col-reverse justify-start">
                    {b.segments.map((s, si) => {
                      const v = s.value ?? 0;
                      const top = si === b.segments.length - 1;
                      return v > 0 ? (
                        <div
                          key={`${s.label}:${si}`}
                          className={cn(
                            `w-full ${TONE_FILL[s.tone]} transition-[height] duration-(--dur) ease-(--ease-out)`,
                            top && "rounded-t-(--radius-sm)",
                            TARGET_FOCUS,
                          )}
                          style={{ height: `${Math.max(1.5, (v / max) * 100)}%` }}
                          {...target(
                            `s${bi}:${si}`,
                            {
                              period: b.label,
                              series: s.label,
                              value: formatValue(v),
                              tone: s.tone,
                            },
                            { col: bi, row: si },
                          )}
                        />
                      ) : (
                        // A zero slice has no height to point at; its value is in the sr-only table.
                        <div key={`${s.label}:${si}`} className="w-full" style={{ height: 0 }} />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex gap-1.5">
        {bars.map((b, i) => (
          <span
            key={`${b.label}:x:${i}`}
            className="tabular min-w-0 flex-1 truncate text-center text-(length:--text-2xs) text-(--text-muted)"
          >
            {dense ? (i === 0 || i === bars.length - 1 ? b.label : "") : b.label}
          </span>
        ))}
      </div>

      {/* sr-only table so keyboard / screen-reader users get every value the colour segments encode. */}
      <table className="sr-only">
        <caption>{caption}</caption>
        <thead>
          <tr>
            <th>{caption}</th>
            {seriesLabels.map((l) => (
              <th key={l}>{l}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bars.map((b, i) => (
            <tr key={`${b.label}:sr:${i}`}>
              <td>{b.label}</td>
              {b.segments.map((s, si) => (
                <td key={`${s.label}:sr:${si}`}>{s.value ?? "—"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {bubble}
    </figure>
  );
}
